/**
 * Planning-only TN LLC / S-Corp tax estimates for Finances cashflow.
 * Not tax advice — one profit input, one effective federal rate, no Form 1040.
 */
import type {
  ObligationSettings,
  ObligationSlice,
  PayrollPosition,
  TaxPlan,
} from '../types/finances';
import {
  createDefaultTaxPlan,
  DEFAULT_OBLIGATION_SETTINGS,
  DEFAULT_QBI_RATE,
  DEFAULT_TN_EXCISE_RATE,
  DEFAULT_TN_FRANCHISE_MIN,
  DEFAULT_TN_FRANCHISE_PER_HUNDRED,
} from '../types/finances';
import { resolvePositionForMonth } from './payrollCompSchedule';

export interface TaxPlanBreakdown {
  year: number;
  annualW2: number;
  estimatedProfit: number;
  /** Year-end bonus cash-out (gross + employer tax) already deducted before K-1. */
  bonusDeduction: number;
  k1Remainder: number;
  qbiDeduction: number;
  taxablePassThrough: number;
  federalOwnerEstimate: number;
  federalEntityTax: number;
  tnPersonalIncomeTax: number;
  tnExcise: number;
  tnFranchise: number;
  tnFranchiseAndExcise: number;
  companyCashTotal: number;
}

export interface ComputedTaxPlan {
  year: number;
  enabled: boolean;
  breakdown: TaxPlanBreakdown;
  slices: ObligationSlice[];
}

function roundMoney(n: number): number {
  return Math.round(n);
}

function clampDay(year: number, month1to12: number, day: number): Date {
  const monthIdx = Math.min(12, Math.max(1, month1to12)) - 1;
  const last = new Date(year, monthIdx + 1, 0).getDate();
  const d = Math.min(last, Math.max(1, Math.round(day) || 1));
  return new Date(year, monthIdx, d);
}

/** Federal 1040-ES dates for a tax year: Apr 15, Jun 15, Sep 15, Jan 15 (next year). */
export function federalEstimatedDueDates(taxYear: number): Date[] {
  return [
    new Date(taxYear, 3, 15),
    new Date(taxYear, 5, 15),
    new Date(taxYear, 8, 15),
    new Date(taxYear + 1, 0, 15),
  ];
}

/**
 * TN franchise & excise return is due the 15th day of the 4th month after FYE.
 * Calendar year (FYE December) → April 15 of the following year.
 */
export function tnFranchiseAndExciseDueDate(
  taxYear: number,
  fiscalYearEndMonth = 12,
): Date {
  const fye = Math.min(12, Math.max(1, fiscalYearEndMonth));
  const dueMonth = ((fye - 1 + 4) % 12) + 1;
  const dueYear = fye + 4 > 12 ? taxYear + 1 : taxYear;
  return clampDay(dueYear, dueMonth, 15);
}

export function annualPayrollW2(
  positions: PayrollPosition[],
  asOf: Date,
): number {
  return positions.reduce((sum, p) => {
    if (!p.isActive) return sum;
    if (p.status !== 'filled' && p.status !== 'open' && p.status !== 'planned') return sum;
    const resolved = resolvePositionForMonth(p, asOf);
    return sum + Math.max(0, resolved.annualSalary || 0);
  }, 0);
}

export function computeTaxBreakdown(
  plan: TaxPlan,
  annualW2: number,
  bonusDeduction = 0,
): TaxPlanBreakdown {
  const profit = Math.max(0, Number(plan.estimatedAnnualProfit) || 0);
  const w2 = Math.max(0, annualW2);
  const bonuses = Math.max(0, bonusDeduction);
  // Net profit is already after regular W-2. Only year-end bonuses still need to come off.
  const k1Remainder = Math.max(0, profit - bonuses);
  const qbiRate = plan.qbiEnabled
    ? Math.min(1, Math.max(0, Number(plan.qbiRate) || DEFAULT_QBI_RATE))
    : 0;
  const qbiDeduction = roundMoney(k1Remainder * qbiRate);
  const taxablePassThrough = Math.max(0, k1Remainder - qbiDeduction);
  const fedRate = Math.min(1, Math.max(0, Number(plan.ownerFederalEffectiveRate) || 0));
  const federalOwnerEstimate = roundMoney(taxablePassThrough * fedRate);

  const exciseRate = Number(plan.tnExciseRate);
  const tnExcise = roundMoney(k1Remainder * (Number.isFinite(exciseRate) ? exciseRate : DEFAULT_TN_EXCISE_RATE));

  const netWorth = Math.max(0, Number(plan.estimatedNetWorth) || 0);
  const perHundred = Number.isFinite(plan.tnFranchisePerHundred)
    ? plan.tnFranchisePerHundred
    : DEFAULT_TN_FRANCHISE_PER_HUNDRED;
  const minFranchise = Number.isFinite(plan.tnFranchiseMin)
    ? plan.tnFranchiseMin
    : DEFAULT_TN_FRANCHISE_MIN;
  const skipFranchise = netWorth <= 0 && profit <= 0;
  const rawFranchise = roundMoney((netWorth / 100) * perHundred);
  const tnFranchise = skipFranchise ? 0 : Math.max(minFranchise, rawFranchise);

  const companyFederal = plan.includeOwnerTaxDistributions ? federalOwnerEstimate : 0;
  const companyCashTotal = companyFederal + tnExcise + tnFranchise;

  return {
    year: plan.year,
    annualW2: roundMoney(w2),
    estimatedProfit: roundMoney(profit),
    bonusDeduction: roundMoney(bonuses),
    k1Remainder: roundMoney(k1Remainder),
    qbiDeduction,
    taxablePassThrough: roundMoney(taxablePassThrough),
    federalOwnerEstimate,
    federalEntityTax: 0,
    tnPersonalIncomeTax: 0,
    tnExcise,
    tnFranchise,
    tnFranchiseAndExcise: tnExcise + tnFranchise,
    companyCashTotal,
  };
}

export function computeTaxPlan(
  plan: TaxPlan,
  positions: PayrollPosition[],
  options: {
    fiscalYearEndMonth?: number;
    now?: Date;
    /** Deductible year-end bonuses + employer tax. Sized off pre-bonus profit. */
    bonusDeduction?: number;
  } = {},
): ComputedTaxPlan {
  const now = options.now ?? new Date();
  const asOf = new Date(plan.year, 11, 15);
  const annualW2 = annualPayrollW2(positions, asOf);
  const breakdown = computeTaxBreakdown(plan, annualW2, options.bonusDeduction ?? 0);
  const slices: ObligationSlice[] = [];

  if (plan.enabled) {
    const windowStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const push = (slice: ObligationSlice) => {
      if (slice.amount <= 0) return;
      if (slice.dueDate < windowStart) return;
      slices.push(slice);
    };

    if (plan.includeOwnerTaxDistributions && breakdown.federalOwnerEstimate > 0) {
      const dates = federalEstimatedDueDates(plan.year);
      let allocated = 0;
      dates.forEach((dueDate, idx) => {
        const isLast = idx === dates.length - 1;
        const amount = isLast
          ? breakdown.federalOwnerEstimate - allocated
          : roundMoney(breakdown.federalOwnerEstimate / dates.length);
        allocated += amount;
        push({
          plannedKind: 'tax',
          projectLabel: 'Tax',
          label: `Federal estimated tax Q${idx + 1}`,
          amount,
          dueDate,
          estimated: true,
        });
      });
    }

    if (breakdown.tnFranchiseAndExcise > 0) {
      push({
        plannedKind: 'tax',
        projectLabel: 'Tax',
        label: 'TN franchise & excise',
        amount: breakdown.tnFranchiseAndExcise,
        dueDate: tnFranchiseAndExciseDueDate(plan.year, options.fiscalYearEndMonth ?? 12),
        estimated: true,
      });
    }
  }

  return { year: plan.year, enabled: plan.enabled, breakdown, slices };
}

export function taxPlanFromSettings(
  year: number,
  settings: ObligationSettings = DEFAULT_OBLIGATION_SETTINGS,
): TaxPlan {
  return createDefaultTaxPlan(year, settings);
}

/** Calendar years the forecast window can land planned obligations in. */
export function obligationYearsForHorizon(horizon: number, now: Date = new Date()): number[] {
  const start = now.getFullYear();
  const end = new Date(now.getFullYear(), now.getMonth() + Math.max(1, horizon), 1).getFullYear();
  const years: number[] = [];
  for (let y = start; y <= end; y++) years.push(y);
  return years;
}

/** Trailing QB net-income hint for the profit input. */
export function hintAnnualProfitFromCashflow(
  months: string[] | undefined,
  totals: Record<string, { netIncome?: number }> | undefined,
): number | null {
  if (!months?.length || !totals) return null;
  const last12 = months.slice(-12);
  if (last12.length === 0) return null;
  const sum = last12.reduce((s, key) => s + (Number(totals[key]?.netIncome) || 0), 0);
  return roundMoney(sum);
}
